import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ThumbsUp, ThumbsDown, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useShift } from '../contexts/ShiftContext';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ROUTES } from '../constants/routes';
import { submitFeedback } from '../services/feedbackService';

export default function ProfileScreen() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isOnShift, startShift, endShift } = useShift();
  const { nurse, markAllPatientsShiftEnded } = useSession();
  const { language, isRtl, t, saveLanguage } = useLanguage();
  const [feedback, setFeedback] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [languageNotice, setLanguageNotice] = useState(false);
  const Chevron = isRtl ? ChevronLeft : ChevronRight;

  useEffect(() => {
    if (!languageNotice) return undefined;
    const timer = window.setTimeout(() => setLanguageNotice(false), 2500);
    return () => window.clearTimeout(timer);
  }, [languageNotice]);

  const handleLanguageChange = (lang) => {
    if (lang === language) return;
    saveLanguage(lang);
    setLanguageNotice(true);
  };

  const handleEndShift = () => {
    markAllPatientsShiftEnded();
    endShift();
    navigate(ROUTES.SHIFT_HANDOVER_GENERATING);
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim()) {
      setFeedbackError(t('feedbackRequired'));
      return;
    }
    if (!feedback) return;

    setFeedbackSubmitting(true);
    setFeedbackError('');
    setSubmitted(false);

    try {
      await submitFeedback({
        rating: feedback,
        text: feedbackText.trim(),
        language,
        nurseName: nurse.name,
        nurseRole: nurse.role,
        nurseHospital: nurse.hospital,
      });
      setFeedbackText('');
      setFeedback(null);
      setSubmitted(true);
    } catch {
      setFeedbackError(t('feedbackSubmitError'));
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-page pb-8">
      <header className="border-b border-gray-200 bg-surface px-4 py-3">
        <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-2 text-primary">
          <ArrowLeft className={`h-5 w-5 ${isRtl ? 'rotate-180' : ''}`} />
          {t('profile')}
        </button>
      </header>

      <div className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-primary-mid text-2xl font-bold text-primary">
            {nurse.initials}
          </div>
          <h1 className="text-[22px] font-bold" dir="auto">
            {nurse.name}
          </h1>
          <p className="text-sm text-gray-500">{nurse.role}</p>
          {nurse.hospital ? <p className="text-sm text-gray-500">{nurse.hospital}</p> : null}
        </div>

        <div className="card-surface p-4">
          <div className="mb-3 flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${isOnShift ? 'bg-shift-on' : 'bg-gray-300'}`} />
            <span className="font-semibold">{isOnShift ? t('onShift') : t('offShift')}</span>
          </div>
          {isOnShift ? (
            <button
              type="button"
              onClick={handleEndShift}
              className="w-full rounded-xl bg-critical-light py-3 font-semibold text-critical"
            >
              {t('endShift')}
            </button>
          ) : (
            <button type="button" onClick={startShift} className="btn-primary">
              {t('startShift')}
            </button>
          )}
        </div>

        <div className="card-surface p-4">
          <p className="text-section-label mb-3">{t('helpAndTutorial')}</p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ONBOARDING, { state: { fromProfile: true } })}
            className="flex w-full items-center justify-between rounded-xl border-2 border-primary px-4 py-3 text-left"
          >
            <span className="flex items-center gap-2 font-medium text-primary">
              <BookOpen className="h-5 w-5" />
              {t('viewTutorialAgain')}
            </span>
            <Chevron className="h-5 w-5 text-primary" />
          </button>
        </div>

        <div className="card-surface p-4">
          <p className="text-section-label mb-2">{t('language')}</p>
          <div className="flex gap-2">
            {['en', 'ar'].map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => handleLanguageChange(lang)}
                className={`rounded-pill px-4 py-2 text-sm font-semibold ${
                  language === lang ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {lang === 'en' ? t('english') : t('arabic')}
              </button>
            ))}
          </div>
          {languageNotice && (
            <p className="mt-2 text-sm font-medium text-success">{t('languageChanged')}</p>
          )}
        </div>

        <div className="card-surface p-4">
          <p className="text-section-label mb-3">{t('feedback')}</p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setFeedback('up');
                setSubmitted(false);
              }}
              className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 ${
                feedback === 'up' ? 'border-primary bg-primary-light' : 'border-gray-200'
              }`}
            >
              <ThumbsUp className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setFeedback('down');
                setSubmitted(false);
              }}
              className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 ${
                feedback === 'down' ? 'border-primary bg-primary-light' : 'border-gray-200'
              }`}
            >
              <ThumbsDown className="h-5 w-5" />
            </button>
          </div>
          {feedback && (
            <>
              <textarea
                value={feedbackText}
                onChange={(e) => {
                  setFeedbackText(e.target.value);
                  if (feedbackError) setFeedbackError('');
                }}
                placeholder={t('feedbackPlaceholder')}
                rows={3}
                className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
              />
              {feedbackError && (
                <p className="mt-2 text-sm text-critical">{feedbackError}</p>
              )}
              <button
                type="button"
                onClick={handleFeedbackSubmit}
                disabled={feedbackSubmitting}
                className="btn-primary mt-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {feedbackSubmitting ? t('feedbackSubmitting') : t('submit')}
              </button>
            </>
          )}
          {submitted && (
            <p className="mt-2 text-sm font-medium text-success">{t('thanksFeedback')}</p>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            logout();
            navigate(ROUTES.LOGIN);
          }}
          className="btn-secondary"
        >
          {t('signOut')}
        </button>
      </div>
    </div>
  );
}
